import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, TrendingUp, Images, Flame, Award, ClipboardList, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const BENEFITS = [
  { icon: TrendingUp, label: "Save your progress & score history" },
  { icon: ClipboardList, label: "Weekly AI coaching plan" },
  { icon: Images, label: "Before / After comparisons" },
  { icon: Flame, label: "Streaks & weekly challenges" },
  { icon: Award, label: "Achievement badges" },
];

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleLogin = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function AuthModal({ isOpen, onClose, mode = "signup", title, subtitle }) {
  const { loginEmail, register } = useAuth();
  const [tab, setTab] = useState(mode === "login" ? "login" : "signup");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please enter your email and password");
      return;
    }
    setLoading(true);
    try {
      if (tab === "signup") {
        const data = await register(email, password, name);
        if (data.welcome_bonus) {
          toast.success("🎉 Welcome! Your account is ready. Enjoy one extra free AI analysis to start tracking your progress.");
        }
      } else {
        await loginEmail(email, password);
        toast.success("Welcome back! 💅");
      }
      onClose?.(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => onClose?.(false)}
          data-testid="auth-modal"
        >
          <motion.div
            initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-background rounded-3xl p-6 md:p-8 max-w-md w-full max-h-[92vh] overflow-y-auto shadow-2xl relative"
          >
            <button onClick={() => onClose?.(false)} className="absolute top-4 right-4 p-2 text-muted-foreground hover:text-foreground" data-testid="close-auth-btn">
              <X className="w-5 h-5" />
            </button>

            <div className="text-center mb-5">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-pink-400 to-purple-500 mb-3">
                <Sparkles className="w-7 h-7 text-white" />
              </div>
              <h2 className="font-playfair text-2xl font-bold text-foreground">
                {title || "Create your free account"}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {subtitle || "Save your progress and unlock your AI Nail Coach"}
              </p>
            </div>

            {tab === "signup" && (
              <div className="grid grid-cols-1 gap-2 mb-5">
                {BENEFITS.map((b, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm text-foreground/80">
                    <span className="w-7 h-7 rounded-lg bg-secondary/60 flex items-center justify-center text-primary">
                      <b.icon className="w-4 h-4" />
                    </span>
                    {b.label}
                  </div>
                ))}
              </div>
            )}

            <Button
              onClick={startGoogleLogin}
              className="w-full rounded-full py-6 bg-white text-foreground border border-border hover:bg-secondary/40 shadow-sm flex items-center justify-center gap-3"
              data-testid="google-login-btn"
            >
              <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="" className="w-5 h-5" />
              Continue with Google
            </Button>

            <div className="flex items-center gap-3 my-4">
              <div className="h-px bg-border flex-1" />
              <span className="text-xs text-muted-foreground">or</span>
              <div className="h-px bg-border flex-1" />
            </div>

            <form onSubmit={submit} className="space-y-3">
              {tab === "signup" && (
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name (optional)" className="rounded-xl py-5" data-testid="auth-name-input" />
              )}
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="rounded-xl py-5" data-testid="auth-email-input" />
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className="rounded-xl py-5" data-testid="auth-password-input" />
              <Button type="submit" disabled={loading} className="w-full rounded-full py-6 text-base font-semibold bg-primary hover:bg-primary/90" data-testid="auth-submit-btn">
                {loading ? "Please wait..." : (
                  <span className="flex items-center gap-2"><Mail className="w-4 h-4" />{tab === "signup" ? "Create account" : "Log in"}</span>
                )}
              </Button>
            </form>

            <p className="text-center text-sm text-muted-foreground mt-4">
              {tab === "signup" ? "Already have an account?" : "New here?"}{" "}
              <button onClick={() => setTab(tab === "signup" ? "login" : "signup")} className="text-primary font-semibold" data-testid="auth-toggle-tab">
                {tab === "signup" ? "Log in" : "Create account"}
              </button>
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
